/**
 * useAdminPageLevels — the Account's level for each Admin page (Editable,
 * Locked, Hidden), read from `adminPageLevels` on `GET /account/modules`.
 *
 * Fail-CLOSED, like the opt-in `explicit` modules: the map counts only from a
 * FRESH fetch (`isModulesFresh` — a real response landed this session and
 * nothing newer failed). The persisted authStore seed never carries levels, so
 * the seed alone, a load in flight, or a failed fetch all leave every keyed
 * Admin page out of the menu. A key missing from a fresh map is Hidden.
 *
 * Consumers: the nav filter (useFilteredNavItems) and the route guard
 * (RequireAdminPage), which adds its own hold-once rule on top.
 */
import { useMemo } from 'react';
import { isModulesFresh, useAccountModules } from '@/hooks/useAccountModules';
import type { AdminPageLevel, AdminPageLevelMap } from '@/services/accountModules.service';

export type { AdminPageLevel, AdminPageLevelMap } from '@/services/accountModules.service';

export interface AdminPageLevels {
  /**
   * - `fresh`: a real fetch succeeded and nothing newer failed; `levels` holds
   *   the map.
   * - `pending`: no fresh answer yet, and a fetch is on its way (first load,
   *   the persisted seed alone, or a refetch running after a failure).
   * - `failed`: no fresh answer and none on its way (the fetch failed).
   */
  status: 'fresh' | 'pending' | 'failed';
  /** The fresh map, or null unless `status` is `fresh`. Read it with `adminPageLevelOf`. */
  levels: AdminPageLevelMap | null;
}

const NO_LEVELS: AdminPageLevelMap = Object.freeze({});

/**
 * One page's level in a fresh map. Anything other than `editable` or `locked`
 * (a missing key, `hidden`, an unknown value) reads as Hidden.
 */
export function adminPageLevelOf(levels: AdminPageLevelMap, key: string): AdminPageLevel {
  const level = levels[key];
  return level === 'editable' || level === 'locked' ? level : 'hidden';
}

export function useAdminPageLevels(): AdminPageLevels {
  const query = useAccountModules();
  const fresh = isModulesFresh(query);
  const raw = query.data?.adminPageLevels;
  const status: AdminPageLevels['status'] = fresh ? 'fresh' : query.isFetching ? 'pending' : 'failed';
  // `raw` keeps its identity across refetches that return the same map
  // (React Query's structural sharing), so the nav memo only reruns on change.
  const levels = fresh ? (raw ?? NO_LEVELS) : null;
  return useMemo(() => ({ status, levels }), [status, levels]);
}
